import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const ProtectedRoute = ({
  children,
  allowedUserTypes = [],
  requiredAccessRight = null,
  superAdminOnly = false,
  userOnly = false, // New prop for user-only routes
  adminRestrictedRoutes = [], // Routes that admin cannot access regardless of access rights
  fallbackPath = "/no-access",
  loginPath = "/login-arena",
}) => {
  // Get user from AuthContext (JWT is in httpOnly cookie)
  const { user, loading } = useAuth();

  if (loading) return null; // or a spinner if desired

  const isAuthenticated = !!user;
  const isAdmin = !!user?.isAdmin;
  const isTester = !!user?.isTester;
  const adminType = user?.adminRole;
  const userType = user?.userType;
  const accessRights = user?.accessRights || {};
  const testerAccessRights = user?.testerAccessRights || {};

  // Check if user is authenticated
  if (!isAuthenticated) {
    return <Navigate to={loginPath} replace />;
  }

  // Determine current user type
  let currentUserType = "user";
  if (isAdmin) {
    currentUserType = adminType?.toLowerCase() || "admin";
  } else if (isTester) {
    currentUserType = "tester";
  }

  // Check for user-only routes (admins and superadmins cannot access)
  if (userOnly) {
    if (currentUserType === "admin" || currentUserType === "superadmin") {
      return <Navigate to={fallbackPath} replace />;
    }

    // For testers, check if they have the required access right (if specified)
    if (currentUserType === "tester" && requiredAccessRight) {
      // Check if tester has "all" access (bypass specific checks)
      if (testerAccessRights.all) {
        return children;
      }

      // Check specific access right
      if (!testerAccessRights[requiredAccessRight]) {
        return <Navigate to={fallbackPath} replace />;
      }
    }
  }

  // Check if user type is allowed
  if (
    allowedUserTypes.length > 0 &&
    !allowedUserTypes.includes(currentUserType)
  ) {
    return <Navigate to={fallbackPath} replace />;
  }

  // SuperAdmin gets access to everything (except when explicitly restricted or userOnly)
  if (currentUserType === "superadmin" && !superAdminOnly && !userOnly) {
    return children;
  }

  // Check for SuperAdmin-only routes
  if (superAdminOnly && currentUserType !== "superadmin") {
    return <Navigate to={fallbackPath} replace />;
  }

  // Check admin restricted routes (routes admin can never access)
  if (currentUserType === "admin" && adminRestrictedRoutes.length > 0) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Check specific access rights for admin
  if (currentUserType === "admin" && requiredAccessRight) {
    if (!accessRights[requiredAccessRight]) {
      return <Navigate to={fallbackPath} replace />;
    }
  }

  // Check specific access rights for tester
  if (currentUserType === "tester" && requiredAccessRight) {
    if (!testerAccessRights[requiredAccessRight]) {
      return <Navigate to={fallbackPath} replace />;
    }
  }

  // For regular users and testers - they shouldn't access admin routes (unless userOnly is true)
  if (
    (currentUserType === "user" || currentUserType === "tester") &&
    !userOnly &&
    (allowedUserTypes.includes("admin") ||
      allowedUserTypes.includes("superadmin"))
  ) {
    return <Navigate to={fallbackPath} replace />;
  }

  return children;
};

export default ProtectedRoute;
